import { resolveAllPromptIssues } from '@/lib/promptInlineAnalyzer';
import { analyzePromptPhysics, fixPromptPhysics, type PhysicsIssue } from '@/lib/promptPhysicsEngine';
import { buildCinematicGrammarReport, type CinematicGrammarReport } from '@/lib/cinematicGrammarEngine';
import { buildVisualSceneGraph, type VisualSceneGraph } from '@/lib/visualSceneGraphEngine';

export type HollywoodQualityLevel = 'Basic' | 'Intermediate' | 'Professional' | 'Studio Quality' | 'Hollywood Level';

export type HollywoodPromptReport = {
  parsedPrompt: string;
  physics: PhysicsIssue[];
  grammar: CinematicGrammarReport;
  sceneGraph: VisualSceneGraph;
  contradictions: string[];
  enhancedPrompt: string;
  finalOptimizedPrompt: string;
  score: number;
  qualityLevel: HollywoodQualityLevel;
  styleIntelligence: {
    detectedStyle: string;
    styleHints: string[];
  };
  atmosphereHints: string[];
  colorGradingHints: string[];
};

const detectStyle = (prompt: string): { detectedStyle: string; styleHints: string[] } => {
  const lower = prompt.toLowerCase();

  if (/anime|manga/.test(lower)) {
    return { detectedStyle: 'anime', styleHints: ['cinematic anime lighting', 'dynamic cel-shaded contrast', 'stylized motion framing'] };
  }
  if (/concept art/.test(lower)) {
    return { detectedStyle: 'concept art', styleHints: ['painterly cinematic blocking', 'production concept composition', 'hero asset detailing'] };
  }
  if (/fantasy illustration|fantasy/.test(lower)) {
    return { detectedStyle: 'fantasy illustration', styleHints: ['mythic atmosphere', 'epic environmental layering', 'arcane lighting accents'] };
  }
  if (/photoreal|photorealistic/.test(lower)) {
    return { detectedStyle: 'photorealistic', styleHints: ['physically plausible materials', 'filmic tonal mapping', 'camera-accurate lens behavior'] };
  }
  return { detectedStyle: 'cinematic realism', styleHints: ['cinematic framing', 'motivated film lighting', 'story-driven blocking'] };
};

const ensureAtmosphere = (prompt: string): string => {
  const additions: string[] = [];
  if (!/fog|mist|haze/.test(prompt.toLowerCase())) additions.push('subtle atmospheric fog');
  if (!/dust|particles/.test(prompt.toLowerCase())) additions.push('fine dust particles');
  if (!/volumetric/.test(prompt.toLowerCase())) additions.push('volumetric light shafts');
  if (!/film grain/.test(prompt.toLowerCase())) additions.push('fine film grain');
  return additions.length ? `${prompt}. ${additions.join(', ')}` : prompt;
};

const ensureColorGrade = (prompt: string): { prompt: string; hints: string[] } => {
  const lower = prompt.toLowerCase();
  const hints: string[] = [];

  if (!/teal and orange|warm sunset|neon cyberpunk/.test(lower)) {
    hints.push('teal and orange cinematic grade');
  }

  if (!/color grade|grading/.test(lower)) {
    return { prompt: `${prompt}. color grading: ${hints[0] || 'warm sunset tones'}`, hints };
  }

  return { prompt, hints };
};

const buildScore = (physicsCount: number, contradictionCount: number, grammarScore: number, graphDensity: number): { score: number; qualityLevel: HollywoodQualityLevel } => {
  const penalties = physicsCount * 7 + contradictionCount * 8;
  const graphBonus = Math.min(8, Math.round(graphDensity * 20));
  const score = Math.max(0, Math.min(100, grammarScore + graphBonus - penalties));

  if (score >= 90) return { score, qualityLevel: 'Hollywood Level' };
  if (score >= 75) return { score, qualityLevel: 'Studio Quality' };
  if (score >= 60) return { score, qualityLevel: 'Professional' };
  if (score >= 40) return { score, qualityLevel: 'Intermediate' };
  return { score, qualityLevel: 'Basic' };
};

export const runHollywoodPromptEngine = (rawPrompt: string): HollywoodPromptReport => {
  const parsedPrompt = rawPrompt.trim();
  const physics = analyzePromptPhysics(parsedPrompt);
  const physicsFixed = fixPromptPhysics(parsedPrompt, physics);

  const grammar = buildCinematicGrammarReport(physicsFixed);
  const sceneGraph = buildVisualSceneGraph(grammar.enhancedPrompt);

  const contradictions = [
    ...grammar.conflicts,
    ...sceneGraph.conflicts.map((c) => c.message),
    ...physics.map((p) => `[${p.category}] ${p.explanation}`),
  ];

  const styleIntelligence = detectStyle(grammar.enhancedPrompt);
  const atmospherePrompt = ensureAtmosphere(grammar.enhancedPrompt);
  const { prompt: colorPrompt, hints: colorGradingHints } = ensureColorGrade(atmospherePrompt);

  const finalOptimizedPrompt = resolveAllPromptIssues(`${colorPrompt}. style intelligence: ${styleIntelligence.detectedStyle}`)
    .replace(/\s{2,}/g, ' ')
    .trim();

  const { score, qualityLevel } = buildScore(physics.length, contradictions.length, grammar.score, sceneGraph.metrics.density);

  return {
    parsedPrompt,
    physics,
    grammar,
    sceneGraph,
    contradictions,
    enhancedPrompt: grammar.enhancedPrompt,
    finalOptimizedPrompt,
    score,
    qualityLevel,
    styleIntelligence,
    atmosphereHints: ['fog', 'dust', 'particles', 'volumetric lighting'],
    colorGradingHints: colorGradingHints.length ? colorGradingHints : ['warm sunset tones', 'teal and orange', 'neon cyberpunk palettes'],
  };
};
